import { assembleWireMap, FrameAssembler, MAX_MAP_PARTS, type WireFrame, type WireMap, type WireMapMessage } from './outputViewCodec';

/** What the controller says the output path is doing (`wire`, and `status.wire`). */
export type OutputViewWireState = 'live' | 'blackout' | 'held';
/** Why the wire is not live; carried by `status` only (controller 1.7.36, Amendment 1). */
export type OutputViewWireReason = 'no_link' | 'no_address' | 'send_failed' | 'blackout';
/** One wire report from the controller. A `wire` change message carries no reason; `status` carries it at 1 Hz. */
export interface OutputViewWireReport {
  state: OutputViewWireState;
  reason?: OutputViewWireReason;
  thinnedFrames?: string;
  from: 'wire' | 'status';
}
export type OutputViewMapState = 'connected-no-map' | 'map-failed';

const WIRE_STATES: ReadonlySet<string> = new Set(['live', 'blackout', 'held']);
const WIRE_REASONS: ReadonlySet<string> = new Set(['no_link', 'no_address', 'send_failed', 'blackout']);
/** Without a complete map this long after the view went live, the view says it is connected and waiting. */
const MAP_WAIT_MS = 5_000;

export interface OutputViewStreamCallbacks {
  onFrame: (frame: WireFrame, clockOffsetUs: bigint) => void;
  onMap: (map: WireMap | null) => void;
  onWire: (report: OutputViewWireReport) => void;
  onMapState?: (state: OutputViewMapState | null) => void;
  /** Tell the controller the map this viewer now holds (output-view/2 `ready`). */
  sendReady: (mapDigest: string) => void;
}

interface StreamMessage { type?: string; [key: string]: unknown }

/**
 * The output-view/2 picture once a view is live, whichever line carries it: the legacy session's own peer
 * (`OutputViewPeer`) or the room link (`RoomLink`, plan 2026-10-21-plan-controller-session-link D14). It holds
 * the wire map's parts until the map is whole, answers `ready`, reads `wire` and `status`, and assembles frames.
 * The bytes and their meaning are output-view/2's (`contracts/output-view/2`); only the carrier differs.
 */
export class OutputViewStream {
  private readonly assembler = new FrameAssembler();
  private readonly mapParts = new Map<number, WireMapMessage>();
  private mapDigest = '';
  private mapState: OutputViewMapState | null = null;
  private mapTimer?: ReturnType<typeof setTimeout>;
  private live = false;
  private stopped = false;

  constructor(private readonly callbacks: OutputViewStreamCallbacks, private readonly clockOffsetUs: () => bigint) {}

  /** The controller acknowledged the view: frames count from here, and the map has five seconds to arrive. */
  begin(): void {
    if (this.stopped) return;
    this.live = true;
    this.armMapWait();
  }

  /**
   * The view moved to another line that resends the same map (a link reopened before its ticket expired, D3):
   * the parts start over, but a map with the digest already held is not news, so the drawing stays.
   */
  restart(): void {
    if (this.stopped) return;
    this.mapParts.clear();
    this.armMapWait();
  }

  /** One control-side message; true when it belonged to the picture (a map part, a wire change or a status). */
  control(message: StreamMessage): boolean {
    if (this.stopped || !this.live) return false;
    if (message.type === 'wireMap') { void this.receiveMap(message as unknown as WireMapMessage); return true; }
    if (message.type === 'wire') {
      if (typeof message.state === 'string' && WIRE_STATES.has(message.state)) {
        this.callbacks.onWire({ state: message.state as OutputViewWireState, from: 'wire' });
      }
      return true;
    }
    if (message.type === 'status') {
      if (typeof message.wire === 'string' && WIRE_STATES.has(message.wire)
        && typeof message.thinnedFrames === 'string' && /^(0|[1-9][0-9]{0,19})$/.test(message.thinnedFrames)) {
        const reason = typeof message.reason === 'string' && WIRE_REASONS.has(message.reason)
          ? message.reason as OutputViewWireReason : undefined;
        this.callbacks.onWire({ state: message.wire as OutputViewWireState, reason, thinnedFrames: message.thinnedFrames, from: 'status' });
      }
      return true;
    }
    return false;
  }

  /** One frame-channel message (binary). A damaged copy never changes the wire or the drawing. */
  async frame(raw: unknown): Promise<void> {
    if (this.stopped || !this.live) return;
    try {
      const bytes = (raw instanceof ArrayBuffer || Object.prototype.toString.call(raw) === '[object ArrayBuffer]') ? new Uint8Array(raw as ArrayBuffer)
        : raw instanceof Uint8Array ? raw
          : raw instanceof Blob ? new Uint8Array(await raw.arrayBuffer()) : undefined;
      if (!bytes) return;
      const frame = this.assembler.add(bytes);
      if (frame) this.callbacks.onFrame(frame, this.clockOffsetUs());
    } catch { /* A damaged copy never changes the wire or the drawing. */ }
  }

  stop(): void {
    this.stopped = true;
    if (this.mapTimer) clearTimeout(this.mapTimer);
    this.mapTimer = undefined;
  }

  private armMapWait(): void {
    if (this.mapTimer) clearTimeout(this.mapTimer);
    this.mapTimer = setTimeout(() => {
      this.mapTimer = undefined;
      if (!this.stopped && this.mapState !== 'map-failed') this.updateMapState('connected-no-map');
    }, MAP_WAIT_MS);
  }

  private updateMapState(state: OutputViewMapState | null): void {
    if (this.mapState === state) return;
    this.mapState = state;
    this.callbacks.onMapState?.(state);
  }

  private async receiveMap(message: WireMapMessage): Promise<void> {
    if (!Number.isInteger(message.parts) || message.parts < 1 || message.parts > MAX_MAP_PARTS
      || !Number.isInteger(message.part) || message.part < 0 || message.part >= message.parts) {
      this.callbacks.onMap(null);
      this.updateMapState('map-failed');
      return;
    }
    if (message.mapDigest !== this.mapDigest) {
      this.mapDigest = message.mapDigest;
      this.mapParts.clear();
      this.callbacks.onMap(null);
    }
    if (this.mapParts.has(message.part)) {
      this.mapParts.clear();
      this.callbacks.onMap(null);
      this.updateMapState('map-failed');
      return;
    }
    this.mapParts.set(message.part, message);
    if (this.mapParts.size !== message.parts) return;
    try {
      const map = await assembleWireMap([...this.mapParts.values()]);
      if (this.stopped || this.mapDigest !== map.mapDigest) return;
      if (this.mapTimer) clearTimeout(this.mapTimer);
      this.mapTimer = undefined;
      this.updateMapState(null);
      this.callbacks.onMap(map);
      this.callbacks.sendReady(map.mapDigest);
    } catch {
      if (this.stopped) return;
      this.mapParts.clear();
      this.callbacks.onMap(null);
      this.updateMapState('map-failed');
    }
  }
}
